/**
 * `AigorDrawer` — pannello laterale fisso (destra) in cui **AIgor** conduce la
 * valutazione AI di una Skill sotto forma di **conversazione in chat**.
 *
 * Il Drawer è controllato dal contesto {@link useAigor}: quando `openSkillId`
 * diventa non-`null` avvia il quiz sulla relativa Skill tramite l'hook
 * {@link useAiQuiz} e mostra le domande **una alla volta** (Req. 3.1, 3.2).
 * L'interfaccia è un log di "bolle" di messaggio: AIgor (a sinistra, gradient
 * AI) e utente (a destra, grigio). L'utente risponde **esclusivamente** premendo
 * i pulsanti delle opzioni sotto l'ultima domanda: non esiste alcun campo di
 * input libero (Req. 3.3). Il tempo residuo del timer è sempre visibile mentre
 * il quiz è in corso (Req. 3.5). Al termine mostra il Livello AI stimato; in
 * caso di rifiuto (scaduto, anti-cheating, sessione non valida, servizio non
 * disponibile) mostra un messaggio che invita a ripetere il quiz (Req. 8.2).
 *
 * Lo storico dei messaggi è mantenuto localmente (`messages`) e alimentato via
 * effetti in base allo stato dell'hook: i due messaggi di benvenuto una sola
 * volta per sessione, la battuta "si comincia" una sola volta all'ingresso in
 * fase `running`, e — al momento della risposta — la coppia domanda (AIgor) +
 * risposta scelta (utente) confluisce nello **storico**. La domanda **corrente**
 * (ancora senza risposta) non entra nello storico: è renderizzata come una
 * bolla AIgor dedicata che contiene al suo interno anche i pulsanti opzione.
 *
 * *Validates: Requirements 3.1, 3.2, 3.3, 3.5, 8.2.*
 */
import { useEffect, useId, useRef, useState } from 'react';
import type { Livello } from '../shared/dto';
import { Icon } from './ui';
import { LivelloBadge } from './LivelloBadge';
import { useAigor } from './AigorContext';
import { useAiQuiz } from '../hooks/useAiQuiz';

// Ri-esporta il contesto così che il task 7.4 possa importare tutto da un unico
// punto (`AigorProvider`, `useAigor`) accanto al `AigorDrawer`.
export { AigorProvider, useAigor } from './AigorContext';
export type { AigorContextValue } from './AigorContext';

/**
 * Ritardo (ms) tra una bolla AIgor e la successiva quando la coda viene
 * svuotata: dà alla chat il ritmo di una conversazione reale, mostrando
 * l'indicatore "sta scrivendo…" tra un messaggio e l'altro.
 */
const BUBBLE_DELAY_MS = 500;

/**
 * Ritardo (ms) tra la risposta dell'utente e la comparsa della domanda
 * successiva: durante questa pausa AIgor mostra l'indicatore "sta scrivendo…",
 * dando l'impressione che stia formulando la prossima domanda.
 */
const NEXT_QUESTION_DELAY_MS = 2000;

/** Ruolo di una bolla di messaggio nella chat. */
type ChatRole = 'aigor' | 'user';

/** Singola bolla della conversazione. */
interface ChatMessage {
  id: string;
  role: ChatRole;
  text: string;
  /**
   * Livello AI da mostrare come badge sotto il testo della bolla, con lo stesso
   * stile del risultato finale. Usato per annunciare il Livello AI già stimato
   * nell'onboarding di una Skill già valutata.
   */
  livello?: Livello | null;
}

/**
 * Elemento accodato per la comparsa "a tempo" delle bolle AIgor. Oltre al testo
 * può portare un `livello` AI da renderizzare come badge (vedi {@link ChatMessage}).
 */
interface QueuedMessage {
  text: string;
  livello?: Livello | null;
}

/** Formatta i millisecondi residui come `mm:ss` (mai negativo). */
function formatRemaining(remainingMs: number): string {
  const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const mm = String(minutes).padStart(2, '0');
  const ss = String(seconds).padStart(2, '0');
  return `${mm}:${ss}`;
}

/** Indicatore "AIgor sta scrivendo…": tre punti animati dentro una bolla AIgor. */
function TypingBubble() {
  return (
    <div
      className="aigor-msg aigor-msg--aigor aigor-typing"
      role="status"
      aria-label="AIgor sta scrivendo…"
    >
      <span className="aigor-typing__dot" aria-hidden="true" />
      <span className="aigor-typing__dot" aria-hidden="true" />
      <span className="aigor-typing__dot" aria-hidden="true" />
    </div>
  );
}

/**
 * `AigorDrawer` — pannello fisso sulla destra. Quando nessuna Skill è aperta nel
 * contesto non renderizza nulla; all'apertura avvia il quiz una sola volta per
 * la Skill selezionata e conduce la conversazione in chat.
 */
export function AigorDrawer() {
  const {
    openSkillId,
    openSkillName,
    openSkillDescription,
    openUserName,
    openExistingAiLevel,
    close,
  } = useAigor();
  const quiz = useAiQuiz();
  const titleId = useId();

  // Storico della conversazione (bolle AIgor/utente). Alimentato via effetti.
  const [messages, setMessages] = useState<ChatMessage[]>([]);

  // Coda dei testi AIgor "proattivi" ancora da mostrare (benvenuto, "preparo…",
  // "si comincia"). Un effetto "driver" ne estrae uno ogni BUBBLE_DELAY_MS,
  // così le bolle AIgor compaiono una alla volta come in una chat reale.
  const [queue, setQueue] = useState<QueuedMessage[]>([]);
  // `true` mentre l'indicatore "AIgor sta scrivendo…" deve essere visibile
  // durante lo svuotamento della coda.
  const [isTyping, setIsTyping] = useState(false);

  // `true` una volta che l'utente ha premuto "Inizia il quiz" (o dopo un
  // riavvio da errore): finché è `false` mostriamo la "start gate" con il
  // pulsante di avvio invece di far partire il quiz automaticamente.
  const [started, setStarted] = useState(false);

  // `true` durante la pausa (NEXT_QUESTION_DELAY_MS) dopo una risposta: mostra
  // l'indicatore "sta scrivendo…" e nasconde la domanda live, così la domanda
  // successiva non compare istantaneamente ma dopo il typing.
  const [pendingNext, setPendingNext] = useState(false);
  // Timer della pausa post-risposta, così da poterlo annullare su reset/chiusura.
  const pendingNextTimerRef = useRef<number | null>(null);

  // `start`/`reset` sono stabili (useCallback nell'hook), ma li leggiamo da un
  // ref così l'effetto di avvio può dipendere SOLO da `openSkillId`: senza
  // questo accorgimento i re-render dell'hook (tick del timer) lo
  // ri-eseguirebbero riavviando il quiz.
  const quizActionsRef = useRef({ start: quiz.start, reset: quiz.reset });
  quizActionsRef.current = { start: quiz.start, reset: quiz.reset };

  // Traccia la Skill per cui il quiz è già stato avviato, così l'effetto non
  // ri-avvia lo stesso quiz (guardia anti-riavvio).
  const startedSkillIdRef = useRef<string | null>(null);

  // Traccia la sessione per cui i messaggi di benvenuto sono già stati inseriti
  // (una sola volta per apertura).
  const welcomedSkillIdRef = useRef<string | null>(null);
  // Traccia la sessione per cui la battuta "sto preparando il quiz…" è già
  // stata inserita (una sola volta, all'ingresso in fase `generating`).
  const generatingAnnouncedRef = useRef<string | null>(null);
  // Traccia la sessione per cui la battuta "si comincia" è già stata inserita
  // (una sola volta, all'ingresso in fase `running`).
  const startedConversationRef = useRef<string | null>(null);
  // Insieme degli id delle domande già confluite nello storico al momento della
  // risposta: evita di pubblicare due volte la stessa coppia domanda/risposta
  // sui re-render/tick del timer.
  const answeredQuestionIdsRef = useRef<Set<string>>(new Set());

  // Contatore monotono per generare id univoci delle bolle.
  const seqRef = useRef(0);
  const nextId = (): string => {
    seqRef.current += 1;
    return `m${seqRef.current}`;
  };

  // Accoda un testo AIgor "proattivo": non compare subito, ma verrà mostrato
  // dal driver della coda con il ritardo di conversazione. Le bolle utente e la
  // finalizzazione usano invece `pushMessage`/append immediato.
  const enqueueAigor = (text: string, livello?: Livello | null) => {
    setQueue((prev) => [...prev, { text, livello }]);
  };

  // Azzera la pausa post-risposta (typing tra le domande) e ne annulla il timer.
  const clearPendingNext = () => {
    if (pendingNextTimerRef.current !== null) {
      window.clearTimeout(pendingNextTimerRef.current);
      pendingNextTimerRef.current = null;
    }
    setPendingNext(false);
  };

  // Riferimento all'elemento sentinella in fondo alla lista: consente
  // l'auto-scroll verso l'ultimo messaggio ad ogni aggiornamento della chat.
  const bottomRef = useRef<HTMLDivElement | null>(null);

  // Avvio all'apertura / reset alla chiusura, guidati da `openSkillId`.
  useEffect(() => {
    if (openSkillId !== null) {
      if (startedSkillIdRef.current !== openSkillId) {
        startedSkillIdRef.current = openSkillId;
        // Nuova sessione: azzera lo storico, la coda e le guardie di deduplica.
        // Il quiz NON viene avviato qui: parte solo quando l'utente preme
        // "Inizia il quiz" (start gate).
        setMessages([]);
        setQueue([]);
        setIsTyping(false);
        setStarted(false);
        clearPendingNext();
        welcomedSkillIdRef.current = null;
        generatingAnnouncedRef.current = null;
        startedConversationRef.current = null;
        answeredQuestionIdsRef.current = new Set();
      }
    } else if (startedSkillIdRef.current !== null) {
      // Il Drawer è stato chiuso: azzera lo stato del quiz, la chat e la coda.
      startedSkillIdRef.current = null;
      welcomedSkillIdRef.current = null;
      generatingAnnouncedRef.current = null;
      startedConversationRef.current = null;
      answeredQuestionIdsRef.current = new Set();
      setMessages([]);
      setQueue([]);
      setIsTyping(false);
      setStarted(false);
      clearPendingNext();
      quizActionsRef.current.reset();
    }
  }, [openSkillId]);

  // Bolle di introduzione: pubblicate una sola volta per apertura, appena il
  // Drawer si apre (il quiz NON è ancora stato avviato). Invitano l'utente a
  // premere "Inizia il quiz" per far partire la valutazione.
  useEffect(() => {
    if (openSkillId === null) return;
    if (welcomedSkillIdRef.current === openSkillId) return;
    welcomedSkillIdRef.current = openSkillId;

    const nameGreeting =
      openUserName && openUserName.trim().length > 0
        ? `Ciao ${openUserName}! 👋 Sono AIgor, il tuo valutatore AI.`
        : 'Ciao! 👋 Sono AIgor, il tuo valutatore AI.';
    const skillLabel =
      openSkillName && openSkillName.trim().length > 0
        ? openSkillName
        : 'questa skill';
    const skillDescription =
      openSkillDescription && openSkillDescription.trim().length > 0
        ? openSkillDescription.trim()
        : null;

    // Accodate: compaiono una alla volta, con l'indicatore di scrittura tra
    // l'una e l'altra, gestite dal driver della coda.
    enqueueAigor(nameGreeting);

    // Se la Skill ha già un Livello AI stimato, avvisa l'utente e chiarisci che
    // una nuova valutazione sovrascrive quella precedente (la start gate qui
    // sotto fa da conferma). Altrimenti l'onboarding standard.
    if (openExistingAiLevel !== null) {
      enqueueAigor(
        `Ho già valutato le tue competenze su ${skillLabel}. Il tuo Livello AI attuale è:`,
        openExistingAiLevel,
      );
      // Ricorda cosa si intende per questa skill, se ne abbiamo una descrizione.
      if (skillDescription !== null) {
        enqueueAigor(`Per questa skill si intende: ${skillDescription}`);
      }
      enqueueAigor(
        'Se avvii una nuova valutazione dovrai rispondere di nuovo a tutte le domande e il Livello precedente verrà sostituito da quello nuovo.',
      );
      enqueueAigor(
        'Sono 15 domande a risposta multipla: alcune stimano il tuo Livello sulla skill e altre servono solo a verificare che tu stia rispondendo con attenzione. Avrai un tempo limitato, quindi vai spedito e fidati dell\'istinto!',
      );
    } else {
      enqueueAigor(
        `Ti farò qualche domanda su ${skillLabel} per stimare il tuo Livello.`,
      );
      // Chiarisce cosa si intende per questa skill, se ne abbiamo una descrizione.
      if (skillDescription !== null) {
        enqueueAigor(`Per questa skill si intende: ${skillDescription}`);
      }
      enqueueAigor(
        'Sono 15 domande a risposta multipla: alcune stimano il tuo Livello sulla skill e altre servono solo a verificare che tu stia rispondendo con attenzione. Avrai un tempo limitato, quindi vai spedito e fidati dell\'istinto!',
      );
    }
    // `enqueueAigor` è stabile (usa solo setter di stato); dipendiamo solo dagli
    // input di contenuto.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSkillId, openSkillName, openSkillDescription, openUserName, openExistingAiLevel]);

  // Battuta "sto preparando il quiz…": inserita una sola volta per sessione,
  // quando l'avvio entra in fase `generating` (domande in generazione async).
  // Dà contesto all'indicatore "sta scrivendo…" mostrato durante il polling.
  useEffect(() => {
    if (openSkillId === null || !quiz.generating) return;
    if (generatingAnnouncedRef.current === openSkillId) return;
    generatingAnnouncedRef.current = openSkillId;
    enqueueAigor('Sto preparando il quiz, dammi qualche secondo… 🧠');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSkillId, quiz.generating]);

  // Battuta "si comincia": inserita una sola volta per sessione, appena il quiz
  // entra in fase `running` (subito prima che compaia la prima domanda live).
  // La domanda corrente NON entra nello storico: è renderizzata come bolla
  // dedicata (con le opzioni) alla fine della chat finché non riceve risposta.
  useEffect(() => {
    if (openSkillId === null || !quiz.running) return;
    if (startedConversationRef.current === openSkillId) return;
    startedConversationRef.current = openSkillId;
    enqueueAigor("Perfetto, si comincia! Rispondi scegliendo un'opzione. ⏱️");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSkillId, quiz.running]);

  // Driver della coda: estrae una bolla AIgor ogni BUBBLE_DELAY_MS mostrando nel
  // frattempo l'indicatore "sta scrivendo…". Quando la coda è vuota spegne
  // l'indicatore. Rieseguito ad ogni variazione della coda (append/dequeue).
  useEffect(() => {
    if (queue.length === 0) {
      setIsTyping(false);
      return;
    }
    setIsTyping(true);
    const timerId = window.setTimeout(() => {
      setQueue((q) => {
        const [next, ...rest] = q;
        if (next !== undefined) {
          setMessages((prev) => [
            ...prev,
            { id: nextId(), role: 'aigor', text: next.text, livello: next.livello },
          ]);
        }
        return rest;
      });
    }, BUBBLE_DELAY_MS);
    return () => window.clearTimeout(timerId);
    // `nextId` è stabile (ref). Il driver dipende solo dalla coda.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queue]);

  // Pulizia del timer della pausa post-risposta allo smontaggio del componente.
  useEffect(() => {
    return () => {
      if (pendingNextTimerRef.current !== null) {
        window.clearTimeout(pendingNextTimerRef.current);
      }
    };
  }, []);

  // Auto-scroll verso il fondo ad ogni nuovo messaggio o cambio di fase.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
  }, [
    messages,
    isTyping,
    pendingNext,
    quiz.loading,
    quiz.generating,
    quiz.submitting,
    quiz.done,
    quiz.error,
  ]);

  /** Chiude il Drawer e reimposta lo stato del quiz. */
  const handleClose = () => {
    quiz.reset();
    close();
  };

  /**
   * Ricomincia da capo dopo un rifiuto (quiz scaduto / non affidabile / non
   * disponibile). Svuota **tutta** la chat e riporta l'hook allo stato iniziale
   * (`quiz.reset()`), così la conversazione riparte dall'introduzione
   * (spiegazione del quiz) e dalla start gate "Sei pronto? → Partiamo!". Le
   * bolle di introduzione vengono ri-accodate dall'effetto dedicato perché
   * azzeriamo `welcomedSkillIdRef`.
   */
  const handleRetry = () => {
    if (openSkillId === null) return;
    setMessages([]);
    setQueue([]);
    setIsTyping(false);
    setStarted(false);
    clearPendingNext();
    welcomedSkillIdRef.current = null;
    generatingAnnouncedRef.current = null;
    startedConversationRef.current = null;
    answeredQuestionIdsRef.current = new Set();
    quiz.reset();
  };

  /**
   * Avvia il quiz dalla "start gate". Come per una domanda, la coppia
   * invito (bolla AIgor "Sei pronto?") + scelta dell'utente ("Partiamo!")
   * confluisce nello storico della chat, così resta visibile anche dopo
   * l'avvio; poi segna la sessione come avviata e delega all'hook.
   */
  const handleStart = () => {
    if (openSkillId === null) return;
    setMessages((prev) => [
      ...prev,
      { id: nextId(), role: 'aigor', text: startPromptText },
      { id: nextId(), role: 'user', text: startConfirmText },
    ]);
    setStarted(true);
    void quiz.start(openSkillId);
  };

  /**
   * Registra la risposta: confluisce nello storico la coppia domanda (bolla
   * AIgor) + risposta scelta (bolla utente), poi delega all'hook (che avanza o
   * invia). La domanda è pubblicata nello storico al massimo una volta grazie a
   * `answeredQuestionIdsRef`. Guardia quando non in corso o senza domanda.
   */
  const handleAnswer = (index: number) => {
    if (!quiz.running || quiz.currentQuestion === null) return;
    const question = quiz.currentQuestion;
    const optionText = question.options[index];

    if (!answeredQuestionIdsRef.current.has(question.id)) {
      answeredQuestionIdsRef.current.add(question.id);
      setMessages((prev) => {
        const appended: ChatMessage[] = [
          ...prev,
          { id: nextId(), role: 'aigor', text: question.text },
        ];
        if (optionText !== undefined) {
          appended.push({ id: nextId(), role: 'user', text: optionText });
        }
        return appended;
      });
    }

    const isLast = quiz.currentIndex >= quiz.totalQuestions - 1;
    quiz.answer(index);

    // Tra una domanda e l'altra: mostra i "…" per NEXT_QUESTION_DELAY_MS prima della
    // domanda successiva. Sull'ultima domanda il typing è già garantito dalla
    // fase di invio (`submitting`), quindi non serve la pausa qui.
    if (!isLast) {
      setPendingNext(true);
      if (pendingNextTimerRef.current !== null) {
        window.clearTimeout(pendingNextTimerRef.current);
      }
      pendingNextTimerRef.current = window.setTimeout(() => {
        setPendingNext(false);
        pendingNextTimerRef.current = null;
      }, NEXT_QUESTION_DELAY_MS);
    }
  };

  /**
   * Salta la domanda corrente ("Non lo so"): come {@link handleAnswer} fa
   * confluire nello storico la coppia domanda (bolla AIgor) + "Non lo so" (bolla
   * utente), ma delega a `quiz.skip()` così la domanda resta senza risposta e
   * viene contata come omessa (0 punti) dallo scoring server-side.
   */
  const handleSkip = () => {
    if (!quiz.running || quiz.currentQuestion === null) return;
    const question = quiz.currentQuestion;

    if (!answeredQuestionIdsRef.current.has(question.id)) {
      answeredQuestionIdsRef.current.add(question.id);
      setMessages((prev) => [
        ...prev,
        { id: nextId(), role: 'aigor', text: question.text },
        { id: nextId(), role: 'user', text: 'Non lo so 🤷' },
      ]);
    }

    const isLast = quiz.currentIndex >= quiz.totalQuestions - 1;
    quiz.skip();

    if (!isLast) {
      setPendingNext(true);
      if (pendingNextTimerRef.current !== null) {
        window.clearTimeout(pendingNextTimerRef.current);
      }
      pendingNextTimerRef.current = window.setTimeout(() => {
        setPendingNext(false);
        pendingNextTimerRef.current = null;
      }, NEXT_QUESTION_DELAY_MS);
    }
  };

  if (openSkillId === null) return null;

  const skillLabelForResult =
    openSkillName && openSkillName.trim().length > 0
      ? openSkillName
      : 'questa skill';

  // La "start gate" cambia formulazione se la Skill è già stata valutata: in
  // quel caso AIgor chiede conferma per una NUOVA valutazione (che sovrascrive
  // il voto precedente); altrimenti il classico "Sei pronto?".
  const hasExistingAiLevel = openExistingAiLevel !== null;
  const startPromptText = hasExistingAiLevel
    ? 'Vuoi avviare una nuova valutazione? ⏱️'
    : 'Sei pronto? ⏱️';
  const startConfirmText = hasExistingAiLevel ? 'Sì, ricomincia!' : 'Partiamo!';

  return (
    <aside
      className="aigor-drawer"
      role="dialog"
      aria-modal="false"
      aria-labelledby={titleId}
    >
      <header className="aigor-drawer__header">
        <div className="aigor-drawer__brand">
          <span className="aigor-drawer__avatar" aria-hidden="true">
            <img
              className="aigor-drawer__avatar-img"
              src="/imgs/aigor.png"
              alt=""
            />
          </span>
          <div className="aigor-drawer__brand-text">
            <h2 id={titleId} className="aigor-drawer__title">
              AIgor
              <span className="aigor-drawer__beta">BETA</span>
            </h2>
            <p className="aigor-drawer__subtitle">Valutazione AI della Skill</p>
          </div>
        </div>
        <button
          type="button"
          className="aigor-drawer__close"
          aria-label="Chiudi la valutazione"
          onClick={handleClose}
        >
          <Icon name="close" size={20} />
        </button>
      </header>

      {/* Barra meta: sempre visibile all'apertura del Drawer. Prima dell'avvio
          del quiz mostra placeholder "vuoti" (contatore e timer); durante il
          quiz mostra i valori reali. */}
      <div className="aigor-drawer__meta" role="status">
        <span className="aigor-drawer__progress">
          {quiz.running
            ? `Domanda ${quiz.currentIndex + 1} di ${quiz.totalQuestions}`
            : 'Domanda — di —'}
        </span>
        <span
          className="aigor-drawer__timer"
          aria-label={
            quiz.running
              ? `Tempo residuo: ${formatRemaining(quiz.remainingMs)}`
              : 'Tempo residuo non ancora disponibile'
          }
        >
          ⏱ {quiz.running ? formatRemaining(quiz.remainingMs) : '--:--'}
        </span>
      </div>

      <div className="aigor-drawer__body">
        <div className="aigor-chat">
          {messages.map((message) => (
            <div
              key={message.id}
              className={
                message.role === 'aigor'
                  ? 'aigor-msg aigor-msg--aigor'
                  : 'aigor-msg aigor-msg--user'
              }
            >
              {message.text}
              {message.livello !== undefined && message.livello !== null && (
                <div className="aigor-msg__result-score">
                  <LivelloBadge livello={message.livello} label="Livello AI" />
                </div>
              )}
            </div>
          ))}

          {/* Indicatore di scrittura: durante lo svuotamento della coda, durante
              l'avvio, durante la generazione asincrona delle domande (polling) e
              durante l'invio. Resta un unico elemento alla volta. */}
          {(isTyping ||
            pendingNext ||
            quiz.loading ||
            quiz.generating ||
            quiz.submitting) && <TypingBubble />}

          {/* Domanda corrente (ancora senza risposta): bolla AIgor dedicata che
              contiene il testo della domanda E i pulsanti opzione al suo interno.
              Compare solo quando la coda di introduzione è svuotata, così le
              opzioni non precedono le battute di benvenuto/"si comincia". */}
          {queue.length === 0 &&
            !isTyping &&
            !pendingNext &&
            quiz.running &&
            quiz.currentQuestion !== null && (
            <div className="aigor-msg aigor-msg--aigor">
              <p className="aigor-msg__question">{quiz.currentQuestion.text}</p>
              <ul className="aigor-msg__options">
                {quiz.currentQuestion.options.map((option, index) => (
                  <li key={index}>
                    <button
                      type="button"
                      className="aigor-msg__option"
                      onClick={() => handleAnswer(index)}
                    >
                      {option}
                    </button>
                  </li>
                ))}
                {/* "Non lo so": opzione di minore importanza (stile attenuato),
                    non registra alcuna risposta ⇒ contata come omessa (0 punti). */}
                <li>
                  <button
                    type="button"
                    className="aigor-msg__option aigor-msg__option--skip"
                    onClick={handleSkip}
                  >
                    Non lo so 🤷
                  </button>
                </li>
              </ul>
            </div>
          )}

          {/* Start gate: dopo che le bolle di introduzione sono state mostrate
              (coda svuotata e nessun indicatore di scrittura) e finché il quiz
              non è stato avviato, l'invito e il pulsante di avvio vivono nella
              STESSA bolla AIgor, come una domanda con la sua opzione. */}
          {!started && queue.length === 0 && !isTyping && (
            <div className="aigor-msg aigor-msg--aigor">
              <p className="aigor-msg__question">{startPromptText}</p>
              <ul className="aigor-msg__options">
                <li>
                  <button
                    type="button"
                    className="aigor-msg__option"
                    onClick={handleStart}
                  >
                    {startConfirmText}
                  </button>
                </li>
              </ul>
            </div>
          )}

          {quiz.done && quiz.result !== null && (
            <div className="aigor-msg aigor-msg--aigor">
              {quiz.result.aiLevel !== null ? (
                <>
                  <p className="aigor-msg__question">
                    {`Fatto! 🎉 Ecco il tuo Livello stimato per ${skillLabelForResult}:`}
                  </p>
                  <div className="aigor-msg__result-score">
                    <LivelloBadge livello={quiz.result.aiLevel} label="Livello AI" />
                  </div>
                  <p className="aigor-msg__result-note">
                    Questo Livello viene registrato tra le tue valutazioni e
                    concorre a calcolare lo score definitivo della skill, insieme
                    alla tua autovalutazione e ai voti di reviewer e manager.
                  </p>
                </>
              ) : (
                <p className="aigor-msg__question">
                  {`Fatto! 🎉 Il quiz non ha evidenziato un livello sufficiente su ${skillLabelForResult}. Puoi riprovare quando vuoi.`}
                </p>
              )}
              <ul className="aigor-msg__options">
                <li>
                  <button
                    type="button"
                    className="aigor-msg__option"
                    onClick={handleClose}
                  >
                    Chiudi
                  </button>
                </li>
              </ul>
            </div>
          )}

          {quiz.error !== null && (
            <div className="aigor-msg aigor-msg--aigor">
              <p className="aigor-msg__question">
                {quiz.error} La valutazione non è stata registrata. Vuoi
                ricominciare da capo?
              </p>
              <ul className="aigor-msg__options">
                <li>
                  <button
                    type="button"
                    className="aigor-msg__option"
                    onClick={handleRetry}
                  >
                    Ricomincia
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className="aigor-msg__option"
                    onClick={handleClose}
                  >
                    No, chiudi
                  </button>
                </li>
              </ul>
            </div>
          )}

          <div ref={bottomRef} aria-hidden="true" />
        </div>
      </div>
    </aside>
  );
}

export default AigorDrawer;
